// ABOUTME: Public share page for rendering published investment list reports
// ABOUTME: Standalone page without sidebar, fetches snapshot data via share token

import type { PublishedEntry } from '@alpha-finder/contracts/published';
import { Button, Card, type Column, Table } from '@boundarylab/ui';
import { DownloadSimple, TrendUp } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { publishedApi } from '../lib/api-client';
import { SignalBadge } from '../lib/signal';

const pct = (v: number, digits = 1) => `${(v * 100).toFixed(digits)}%`;
const optionalPct = (v: number | null) => (v != null ? (v * 100).toFixed(1) : '—');

const ENTRY_COLUMNS: Column<PublishedEntry>[] = [
  { key: 'rank', header: '#', render: (_v, e) => <span className="text-gray-400">{e.rank}</span> },
  {
    key: 'symbol',
    header: 'Symbol',
    render: (_v, e) => <span className="font-semibold text-gray-900">{e.symbol}</span>,
  },
  { key: 'signal', header: 'Signal', render: (_v, e) => <SignalBadge signal={e.signal} /> },
  {
    key: 'compositeScore',
    header: 'Score',
    align: 'right',
    render: (_v, e) => pct(e.compositeScore),
  },
  { key: 'confidence', header: 'Confidence', align: 'right', render: (_v, e) => pct(e.confidence) },
  {
    key: 'currentPrice',
    header: 'Price',
    align: 'right',
    render: (_v, e) => `$${e.currentPrice.toFixed(2)}`,
  },
  {
    key: 'predictedReturn',
    header: 'Predicted',
    align: 'right',
    render: (_v, e) => pct(e.predictedReturn, 2),
  },
  { key: 'horizon', header: 'Horizon', render: (_v, e) => e.horizon },
  {
    key: 'factorScore',
    header: 'Factor',
    align: 'right',
    render: (_v, e) => optionalPct(e.factorScore),
  },
  {
    key: 'technicalScore',
    header: 'Technical',
    align: 'right',
    render: (_v, e) => optionalPct(e.technicalScore),
  },
  { key: 'mlScore', header: 'ML', align: 'right', render: (_v, e) => optionalPct(e.mlScore) },
  {
    key: 'alternativeScore',
    header: 'Alt',
    align: 'right',
    render: (_v, e) => optionalPct(e.alternativeScore),
  },
];

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const SIGNAL_TO_INSTRUCTION: Record<string, string> = {
  STRONG_BUY: 'BUY',
  BUY: 'BUY',
  HOLD: 'HOLD',
  SELL: 'SELL',
  STRONG_SELL: 'SELL',
};

function exportAsJson(snapshot: {
  generatedAt: string;
  entryCount: number;
  entries: PublishedEntry[];
}) {
  const feed = {
    version: '1.0',
    generatedAt: snapshot.generatedAt,
    source: "Razvan's Alpha Advisor Pro",
    count: snapshot.entryCount,
    recommendations: snapshot.entries.map((e) => {
      const infoParts: string[] = [
        `Horizon: ${e.horizon}`,
        `Predicted return: ${(e.predictedReturn * 100).toFixed(2)}%`,
        `Confidence: ${(e.confidence * 100).toFixed(1)}%`,
        `Signal: ${e.signal}`,
      ];

      const scores: string[] = [];
      if (e.factorScore != null) scores.push(`factor=${e.factorScore.toFixed(4)}`);
      if (e.mlScore != null) scores.push(`ml=${e.mlScore.toFixed(4)}`);
      if (e.technicalScore != null) scores.push(`technical=${e.technicalScore.toFixed(4)}`);
      if (e.alternativeScore != null) scores.push(`alternative=${e.alternativeScore.toFixed(4)}`);
      if (scores.length > 0) infoParts.push(`Scores: ${scores.join(' | ')}`);

      infoParts.push(`Price: $${e.currentPrice.toFixed(2)}`);

      // Convert symbol to Trading 212 ticker format:
      // MBG.DE -> MBG_DE_EQ, BESI.AS -> BESI_AS_EQ, AAPL -> AAPL_US_EQ
      const dotIdx = e.symbol.indexOf('.');
      const ticker =
        dotIdx !== -1
          ? `${e.symbol.slice(0, dotIdx)}_${e.symbol.slice(dotIdx + 1)}_EQ`
          : `${e.symbol}_US_EQ`;

      return {
        ticker,
        instruction: SIGNAL_TO_INSTRUCTION[e.signal] || 'HOLD',
        info: `${infoParts.join('. ')}.`,
      };
    }),
  };

  const blob = new Blob([JSON.stringify(feed, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `alpha-picks-${snapshot.generatedAt.slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function TopCard({ entry }: { entry: PublishedEntry }) {
  return (
    <Card variant="padded">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-bold text-gray-400">#{entry.rank}</span>
        <SignalBadge signal={entry.signal} />
      </div>
      <div className="text-2xl font-bold text-gray-900 mb-1">{entry.symbol}</div>
      <div className="text-sm text-gray-500 mb-4">${entry.currentPrice.toFixed(2)}</div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <div className="text-gray-400">Score</div>
          <div className="font-semibold">{pct(entry.compositeScore)}</div>
        </div>
        <div>
          <div className="text-gray-400">Confidence</div>
          <div className="font-semibold">{pct(entry.confidence)}</div>
        </div>
      </div>
    </Card>
  );
}

export default function PublishedSharePage() {
  const { shareToken } = useParams<{ shareToken: string }>();

  const {
    data: snapshot,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['published-share', shareToken],
    queryFn: () => publishedApi.getLatestByToken(shareToken!),
    enabled: !!shareToken,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading report...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Report Not Found</h1>
          <p className="text-gray-500">This link is no longer valid or has expired.</p>
        </div>
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">No Reports Yet</h1>
          <p className="text-gray-500">No reports have been generated yet. Check back later.</p>
        </div>
      </div>
    );
  }

  const entries = snapshot.entries || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-6 py-10">
        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-2">
            <TrendUp className="w-7 h-7 text-primary-600" aria-hidden="true" />
            <span className="text-lg font-semibold text-primary-600">Alpha Finder</span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Alpha Picks Report</h1>
              <p className="text-sm text-gray-500">
                Generated {formatDate(snapshot.generatedAt)} &middot; {snapshot.entryCount} entries
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={() => exportAsJson(snapshot)}
              className="inline-flex items-center gap-2"
            >
              <DownloadSimple className="w-4 h-4" aria-hidden="true" />
              Export JSON
            </Button>
          </div>
        </div>

        {entries.length === 0 ? (
          <Card variant="padded" className="text-center">
            <p className="text-gray-500">No entries matched the criteria for this report.</p>
          </Card>
        ) : (
          <>
            {/* Top 3 Cards */}
            {entries.length >= 3 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {entries.slice(0, 3).map((entry) => (
                  <TopCard key={`${entry.symbol}-${entry.horizon}`} entry={entry} />
                ))}
              </div>
            )}

            {/* Full Table */}
            <Card variant="base">
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900">All Entries</h2>
              </div>
              <Table<PublishedEntry>
                columns={ENTRY_COLUMNS}
                data={entries}
                getRowClassName={(_row, i) => (i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50')}
              />
            </Card>
          </>
        )}

        {/* Footer */}
        <div className="mt-10 text-center text-xs text-gray-400">
          Generated by Alpha Finder &middot; {formatDate(snapshot.generatedAt)}
        </div>
      </div>
    </div>
  );
}
